import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Box,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LockResetIcon from "@mui/icons-material/LockReset";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GavelIcon from "@mui/icons-material/Gavel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../redux/reducers/userReducer";
import { changeThemeColor } from "../redux/reducers/themeValueReducer";
import GradientButton from "../components/common/GradiantButton";
import SecondaryButton from "../components/common/SecondaryButton";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";


const Sidebar = ({ collapsed, setCollapsed, drawerWidth, mobileOpen, onDrawerToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getDrawerWidth = () => {
    if (isHovered && collapsed) return 240; // expand on hover
    return drawerWidth;
  };

  const showText = !collapsed || (collapsed && isHovered);

  return (
    <div>
      {/* Sidebar for Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth },
        }}
      >
        <SidebarContent onDrawerToggle={onDrawerToggle} showText={true} />
      </Drawer>

      {/* Sidebar for Desktop */}
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          position: "relative",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Drawer
          variant="permanent"
          sx={{
            "& .MuiDrawer-paper": {
              width: getDrawerWidth(),
              transition: "width 0.2s ease-in-out",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 78px)",
              overflowY: "auto",
              overflowX: "hidden",
              top: "78px",
              position: "fixed",
              borderTopRightRadius: 15,
              borderBottomRightRadius: 15,
            },
          }}
          open
        >
          <SidebarContent
            onDrawerToggle={onDrawerToggle}
            showText={showText}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        </Drawer>
      </Box>
    </div>
  );
};

const SidebarContent = ({ onDrawerToggle, showText, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useSelector((data) => data.themeValue.colors);
  const themeMode = useSelector((state) => state.themeValue.theme);

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const menuItems = [
    ...(isMobile ? [{ text: "Post Property", icon: <AddBoxIcon />, path: "/post-your-property" }] : []),
    { text: "Your Properties", icon: <HomeWorkIcon />, path: "/myproperties" },
    { text: "Vendor Subscription", icon: <MonetizationOnIcon />, path: "/VendorSubscription" },
    { text: "Change Password", icon: <LockResetIcon />, path: "/ChangePassword" },
    { text: "Bank Details", icon: <AccountBalanceIcon />, path: "/bank-details" },
    { text: "Wallet", icon: <AccountBalanceWalletIcon />, path: "/Wallet" },
    { text: "Legal Team", icon: <GavelIcon />, path: "/LegalTeam" },
    { text: "Help", icon: <HelpOutlineIcon />, path: "/Help" },
    // { text: "KYC", icon: <VerifiedUserIcon />, path: "/KYC" },
    { text: "Booked Properties", icon: <HomeWorkIcon />, path: "/bookedproperties" },
    { text: "Referrals List", icon: <PeopleAltOutlinedIcon />, path: "/referrals" },
  ];

  const handleConfirmLogout = () => {
    dispatch(logoutUser());
    navigate("/");
    setOpenLogoutDialog(false);
  };

  const handleToggleTheme = () => {
    dispatch(changeThemeColor(themeMode === "light" ? "dark" : "light"));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onDrawerToggle) onDrawerToggle();
  };

  const togglePin = () => {
    setCollapsed(!collapsed);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.background,
        color: theme.text,
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 🔹 Pin/Unpin Button - Top Right */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            borderBottom: `1px solid ${theme.secondary_text_color}30`,
          }}
        >
          <Tooltip title={collapsed ? "Pin Sidebar" : "Unpin Sidebar"} arrow>
            <IconButton
              onClick={togglePin}
              size="small"
              sx={{
                color: "#799EFF",
                "&:hover": {
                  backgroundColor: "rgba(121, 158, 255, 0.1)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {collapsed ? (
                <PushPinOutlinedIcon fontSize="small" />
              ) : (
                <PushPinIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* 🔹 Scrollable Section */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "3px" },
        }}
      >
        {/* Menu Items */}
        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <Tooltip
                key={item.text}
                title={!showText ? item.text : ""}
                placement="right"
                arrow
              >
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isSelected}
                  sx={{
                    borderRadius: 1.5,
                    px: !showText ? 1.5 : 2,
                    py: 1,
                    justifyContent: !showText ? "center" : "flex-start",
                    backgroundColor:
                      item.text === "Refer & Earn"
                        ? "#fff3cd"
                        : isSelected
                          ? "#bbdefb"
                          : "transparent",
                    "&.Mui-selected": {
                      backgroundColor: "#90caf9",
                      "&:hover": { backgroundColor: "#64b5f6" },
                    },
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "05339C",
                      minWidth: !showText ? "auto" : 40,
                      mr: !showText ? 0 : 1.5,
                      justifyContent: "center",
                      "& svg": {
                        fontSize: "1.6rem",
                        color: "05339C",
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {showText && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "1rem",
                          fontWeight: isSelected ? 400 : 400,
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}


        </List>
        {isMobile && (
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              mb={1}
              color="#19469b"
            >
              Call Us
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Mon – Sun | 9:00 AM – 7:00 PM
            </Typography>

            {/* Number 1 */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton component="a" href="tel:+919629611466">
                <CallIcon sx={{ color: "#19469b" }} />
              </IconButton>
              <Typography fontWeight={500}>+91 9629611466</Typography>
            </Box>

            {/* Number 2 */}
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton component="a" href="tel:+916369102680">
                <CallIcon sx={{ color: "#19469b" }} />
              </IconButton>
              <Typography fontWeight={500}>+91 6369102680</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* 🔹 Fixed Bottom Section */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.secondary_text_color}30`,
          backgroundColor: theme.background,
          position: "sticky",
          bottom: 0,
          zIndex: 1,
        }}
      >
        {/* Night Mode (Commented out as per your previous code) */}
        {/* <Tooltip title={!showText ? "Night Mode" : ""} placement="right" arrow>
          <ListItemButton
            onClick={handleToggleTheme}
            sx={{
              justifyContent: !showText ? "center" : "flex-start",
              mx: 1,
              px: !showText ? 1.5 : 2,
              borderRadius: 1.5,
              py: 1,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
            }}
          >
            <ListItemIcon
              sx={{
                color: "#799EFF",
                minWidth: !showText ? "auto" : 40,
                mr: !showText ? 0 : 1.5,
                justifyContent: "center",
                "& svg": { 
                  fontSize: "1.6rem",
                  color: "#799EFF",
                },
              }}
            >
              <Switch
                size="medium"
                checked={themeMode === "dark"}
                onChange={handleToggleTheme}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#799EFF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#799EFF",
                  },
                }}
              />
            </ListItemIcon>
            {showText && (
              <ListItemText
                primary="Night Mode"
                sx={{
                  "& .MuiTypography-root": { fontSize: "1rem" },
                }}
              />
            )}
          </ListItemButton>
        </Tooltip> */}

        {/* Logout */}
        <Tooltip title={!showText ? "Log Out" : ""} placement="right" arrow>
          <ListItemButton
            onClick={() => setOpenLogoutDialog(true)}
            sx={{
              justifyContent: !showText ? "center" : "flex-start",
              mx: 1,
              px: !showText ? 1.5 : 2,
              borderRadius: 1.5,
              py: 1,
              "&:hover": {
                backgroundColor: "#ef5350",
                color: "#fff",
                "& .MuiListItemIcon-root": {
                  color: "#fff",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "799EFF",
                minWidth: !showText ? "auto" : 40,
                mr: !showText ? 0 : 1.5,
                justifyContent: "center",
                "& svg": {
                  fontSize: "1.6rem",
                  color: "799EFF",
                },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {showText && (
              <ListItemText
                primary="Log Out"
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "1rem",
                  },
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>

      <Dialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {/* Icon */}
          <Box sx={{ mb: 2 }}>
            <LogoutIcon
              sx={{
                fontSize: 64,
                color: '#ff6b6b',
                // background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
                borderRadius: '50%',
                p: 1,
                boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)'
              }}
            />
          </Box>

          {/* Title */}
          <DialogTitle
            sx={{
              p: 0,
              mb: 2,
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#2d3748'
            }}
          >
            Logout Confirmation
          </DialogTitle>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              color: '#718096',
              mb: 0.5,
              lineHeight: 1
            }}
          >
            Are you sure you want to log out?<br />
            You'll need to sign in again to access your account.
          </Typography>
        </Box>

        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
          {/* <Button 
      onClick={() => setOpenLogoutDialog(false)}
      variant="outlined"
      sx={{
        borderRadius: '12px',
        px: 4,
        py: 1,
        border: '2px solid #e2e8f0',
        color: '#4a5568',
        fontWeight: 600,
        '&:hover': {
          border: '2px solid #cbd5e0',
          backgroundColor: '#f7fafc'
        }
      }}
    >
      Cancel
    </Button> */}
          <SecondaryButton
            text="Cancel"
            onClick={() => setOpenLogoutDialog(false)} />
          <GradientButton
            text="Logout"
            onClick={handleConfirmLogout}
          />
          {/* <Button 
      onClick={handleConfirmLogout}
      variant="contained"
      sx={{
        borderRadius: '12px',
        px: 4,
        py: 1,
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
        boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
        fontWeight: 600,
        '&:hover': {
          background: 'linear-gradient(135deg, #ff5252 0%, #ff7b7b 100%)',
          boxShadow: '0 6px 20px rgba(255, 107, 107, 0.6)',
          transform: 'translateY(-1px)'
        },
        transition: 'all 0.3s ease'
      }}
    >
      Logout
    </Button> */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sidebar;